import { useMemo } from 'react';
import { Columns2, Eye, EyeOff, Heart, Maximize2 } from 'lucide-react';
import clsx from 'clsx';
import { useBatch, type GalleryFilter, type GallerySort } from '../../store/batchStore';
import { createRng } from '../../core/rng';
import { getTechnique } from '../../techniques/registry';
import type { Variant } from '../../bulk/types';
import { Segmented, Select } from '../controls/Basics';
import { Slider } from '../controls/Slider';
import { formatDuration } from './BatchConfigPanel';

export function BatchGallery(): JSX.Element {
  const variants = useBatch((s) => s.variants);
  const progress = useBatch((s) => s.progress);
  const thumbSize = useBatch((s) => s.thumbSize);
  const filter = useBatch((s) => s.filter);
  const techniqueFilter = useBatch((s) => s.techniqueFilter);
  const sort = useBatch((s) => s.sort);
  const selection = useBatch((s) => s.selection);
  const compareIds = useBatch((s) => s.compareIds);
  const setThumbSize = useBatch((s) => s.setThumbSize);
  const setFilter = useBatch((s) => s.setFilter);
  const setTechniqueFilter = useBatch((s) => s.setTechniqueFilter);
  const setSort = useBatch((s) => s.setSort);
  const toggleFavourite = useBatch((s) => s.toggleFavourite);
  const toggleHidden = useBatch((s) => s.toggleHidden);
  const select = useBatch((s) => s.select);
  const setDetail = useBatch((s) => s.setDetail);
  const toggleCompare = useBatch((s) => s.toggleCompare);

  const usedTechniques = useMemo(() => {
    const set = new Set<string>();
    for (const v of variants) for (const t of v.meta.techniques) set.add(t);
    return [...set];
  }, [variants]);

  const shown = useMemo(() => {
    let list = variants.filter((v) => {
      if (filter === 'favourites' && !v.favourite) return false;
      if (filter === 'failed' && !v.error) return false;
      if (filter !== 'failed' && v.hidden) return false;
      if (techniqueFilter && !v.meta.techniques.includes(techniqueFilter)) return false;
      return true;
    });
    if (sort === 'random') {
      // Seeded by the batch size so the shuffle is stable while browsing.
      list = createRng(list.length * 7919).shuffle(list);
    } else if (sort === 'speed') {
      list = [...list].sort((a, b) => a.ms - b.ms);
    }
    return list;
  }, [variants, filter, techniqueFilter, sort]);

  const hiddenCount = variants.filter((v) => v.hidden).length;

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-ink-950">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-ink-800 bg-ink-900 px-3 py-2">
        <div className="w-[150px]">
          <Segmented
            size="sm"
            value={filter}
            onChange={(f: GalleryFilter) => setFilter(f)}
            options={[
              { value: 'all', label: 'Alle' },
              { value: 'favourites', label: '♥' },
              { value: 'failed', label: 'Fehler' },
            ]}
          />
        </div>

        {usedTechniques.length > 1 && (
          <div className="w-[170px]">
            <Select
              value={techniqueFilter ?? ''}
              onChange={(v) => setTechniqueFilter(v || null)}
              options={[
                { value: '', label: 'Alle Techniken' },
                ...usedTechniques.map((id) => ({ value: id, label: getTechnique(id)?.name ?? id })),
              ]}
            />
          </div>
        )}

        <div className="w-[150px]">
          <Segmented
            size="sm"
            value={sort}
            onChange={(s: GallerySort) => setSort(s)}
            options={[
              { value: 'order', label: 'Reihe' },
              { value: 'random', label: 'Zufall' },
              { value: 'speed', label: 'Tempo' },
            ]}
          />
        </div>

        <div className="w-[150px]">
          <Slider label="Grösse" value={thumbSize} onChange={(n) => setThumbSize(Math.round(n))} min={90} max={480} step={2} defaultValue={200} precision={0} />
        </div>

        <div className="ml-auto font-mono text-[10px] text-ink-500">
          {shown.length} von {variants.length}
          {hiddenCount > 0 && ` · ${hiddenCount} ausgeblendet`}
          {selection.length > 1 && ` · ${selection.length} ausgewählt`}
        </div>
      </div>

      {/* ── Progress ─────────────────────────────────────────────────────── */}
      {(progress.status === 'running' || progress.status === 'paused') && (
        <div className="shrink-0 border-b border-ink-800 bg-ink-900 px-3 py-2">
          <div className="mb-1 flex items-center gap-3 font-mono text-[10px] text-ink-400">
            <span className="text-ink-200">
              {progress.completed + progress.failed} / {progress.total}
            </span>
            <span>{formatDuration(progress.elapsed)} verstrichen</span>
            {progress.remaining !== null && <span>≈ {formatDuration(progress.remaining)} verbleibend</span>}
            {progress.failed > 0 && <span className="text-amber-400">{progress.failed} fehlgeschlagen</span>}
            <span className="ml-auto truncate text-ink-600">{progress.currentTechnique}</span>
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-ink-800">
            <div
              className={clsx('h-full transition-all', progress.status === 'paused' ? 'bg-ink-500' : 'bg-accent-500')}
              style={{ width: `${((progress.completed + progress.failed) / Math.max(1, progress.total)) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Grid ─────────────────────────────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {variants.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="max-w-[380px] text-center text-[12px] leading-relaxed text-ink-500">
              Noch kein Lauf. Links festlegen, was variieren soll — die Bereiche kommen aus denselben Reglern wie
              im Studio — und starten.
            </p>
          </div>
        ) : (
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${thumbSize}px, 1fr))` }}
          >
            {shown.map((variant) => (
              <Thumb
                key={variant.id}
                variant={variant}
                selected={selection.includes(variant.id)}
                comparing={compareIds.includes(variant.id)}
                onSelect={(additive) => select(variant.id, additive)}
                onOpen={() => setDetail(variant.id)}
                onFavourite={() => toggleFavourite(variant.id)}
                onHide={() => toggleHidden(variant.id)}
                onCompare={() => toggleCompare(variant.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Thumb({
  variant,
  selected,
  comparing,
  onSelect,
  onOpen,
  onFavourite,
  onHide,
  onCompare,
}: {
  variant: Variant;
  selected: boolean;
  comparing: boolean;
  onSelect: (additive: boolean) => void;
  onOpen: () => void;
  onFavourite: () => void;
  onHide: () => void;
  onCompare: () => void;
}): JSX.Element {
  const summary = Object.entries(variant.meta.dimensions).slice(0, 5);

  return (
    <div
      onClick={(e) => onSelect(e.shiftKey || e.metaKey || e.ctrlKey)}
      onDoubleClick={onOpen}
      className={clsx(
        'group/thumb relative overflow-hidden rounded-md border bg-ink-900 transition-colors',
        selected ? 'border-accent-500' : comparing ? 'border-ink-500' : 'border-ink-800 hover:border-ink-600',
        variant.hidden && 'opacity-40',
      )}
    >
      <div className="relative flex aspect-square items-center justify-center bg-ink-950">
        {variant.url ? (
          <img src={variant.url} alt={`Variante ${variant.meta.index}`} className="h-full w-full object-contain" loading="lazy" />
        ) : variant.error ? (
          <span className="px-2 text-center text-[9px] leading-tight text-amber-400">{variant.error}</span>
        ) : (
          <span className="h-4 w-4 animate-pulse rounded-full bg-ink-700" />
        )}

        {/* Hover overlay with the key parameters. */}
        {summary.length > 0 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-full bg-ink-950/92 p-1.5 font-mono text-[9px] leading-tight text-ink-300 transition-transform group-hover/thumb:translate-y-0">
            {summary.map(([key, value]) => (
              <div key={key} className="truncate">
                <span className="text-ink-500">{key}:</span> {typeof value === 'number' ? Number(value.toFixed(4)) : String(value)}
              </div>
            ))}
          </div>
        )}

        <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition-opacity group-hover/thumb:opacity-100">
          <IconButton title="Grossansicht" onClick={onOpen}>
            <Maximize2 size={10} />
          </IconButton>
          <IconButton title="Zum Vergleich hinzufügen" onClick={onCompare} active={comparing}>
            <Columns2 size={10} />
          </IconButton>
          <IconButton title={variant.hidden ? 'Einblenden' : 'Ausblenden'} onClick={onHide}>
            {variant.hidden ? <Eye size={10} /> : <EyeOff size={10} />}
          </IconButton>
        </div>

        <button
          type="button"
          title="Favorit"
          onClick={(e) => {
            e.stopPropagation();
            onFavourite();
          }}
          className={clsx(
            'absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded bg-ink-950/70 transition-opacity',
            variant.favourite ? 'text-red-400 opacity-100' : 'text-ink-300 opacity-0 group-hover/thumb:opacity-100',
          )}
        >
          <Heart size={10} fill={variant.favourite ? 'currentColor' : 'none'} />
        </button>
      </div>

      <div className="flex items-center gap-1 px-1.5 py-1 font-mono text-[9px] text-ink-500">
        <span className="text-ink-400">{String(variant.meta.index).padStart(3, '0')}</span>
        <span className="min-w-0 flex-1 truncate">{variant.meta.word}</span>
        <span>{variant.ms ? `${Math.round(variant.ms)}ms` : ''}</span>
      </div>
    </div>
  );
}

function IconButton({
  title,
  onClick,
  active,
  children,
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={clsx(
        'flex h-5 w-5 items-center justify-center rounded bg-ink-950/70 transition-colors hover:text-ink-100',
        active ? 'text-accent-500' : 'text-ink-300',
      )}
    >
      {children}
    </button>
  );
}
